import { Switch } from "@/components/ui/switch";
import { AlertCircle, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { QuietHoursPanel } from "@/components/QuietHoursPanel";
import { DevicesPanel } from "@/components/DevicesPanel";
import { AllDevicesPanel } from "@/components/AllDevicesPanel";

export function NotificationsSection({ ctx }) {
  const {
    pushEnabled, pushStatus, pushBusy, togglePush,
    runTestPush, reRegisterPush, runPushDiagnostics,
    diagBusy, diagSteps, setDiagSteps, currentUser,
  } = ctx;

  return (
    <>
      <div className="mb-6">
        <h3 className="font-display text-base font-medium mb-2">Notiser</h3>

        {/* iOS Safari-banner: visas när Notification API saknas (vanlig Safari, ej PWA) */}
        {typeof Notification === "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.navigator.standalone && (
          <div className="card-soft p-4 mb-3 bg-[#FFF7E6] border border-[#F5C45E]" data-testid="ios-pwa-banner">
            <p className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              📱 Installera appen för att aktivera notiser
            </p>
            <p className="text-xs text-[color:var(--text-secondary)] mb-2">
              iOS tillåter bara push-notiser när webappen är installerad på hemskärmen. Så här gör du:
            </p>
            <ol className="text-xs text-[color:var(--text-secondary)] space-y-1 ml-4 list-decimal">
              <li>Tryck på <span className="font-medium">dela-ikonen</span> (fyrkant med pil upp) längst ner i Safari</li>
              <li>Välj <span className="font-medium">"Lägg till på hemskärmen"</span></li>
              <li>Öppna Familjen-ikonen <span className="font-medium">från hemskärmen</span></li>
              <li>Gå hit igen och slå på push-notiser</li>
            </ol>
          </div>
        )}

        <div className="card-soft p-4" data-testid="push-settings-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-xl bg-[color:var(--bg-elevated)] flex items-center justify-center flex-shrink-0">
                {pushEnabled ? <Bell size={18} className="text-[color:var(--moss)]" /> : <BellOff size={18} className="text-[color:var(--text-tertiary)]" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Push-notiser</p>
                <p className="text-xs text-[color:var(--text-secondary)] mt-0.5">
                  Få notiser vid nya läxor, prov och delade att-göra.
                </p>
              </div>
            </div>
            <Switch
              checked={pushEnabled}
              onCheckedChange={togglePush}
              disabled={pushBusy || !pushStatus?.supported || !pushStatus?.firebase_configured || !pushStatus?.vapid_configured}
              data-testid="push-toggle"
            />
          </div>

          {pushStatus && (
            <>
              {!pushStatus.supported && (
                <p className="mt-3 text-xs flex items-center gap-1.5 text-[color:var(--text-tertiary)]">
                  <AlertCircle size={12} /> Push stöds inte i denna webbläsare.
                </p>
              )}
              {pushStatus.supported && !pushStatus.firebase_configured && (
                <p className="mt-3 text-xs flex items-center gap-1.5 text-[color:var(--text-tertiary)]">
                  <AlertCircle size={12} /> Firebase ej konfigurerat i Commhub.
                </p>
              )}
              {pushStatus.supported && pushStatus.firebase_configured && !pushStatus.vapid_configured && (
                <p className="mt-3 text-xs flex items-center gap-1.5 text-[#E07A5F]">
                  <AlertCircle size={12} /> VAPID-nyckel saknas. Lägg till den i Commhub Dashboard → Push.
                </p>
              )}

              {/* Status-rad — alltid synlig */}
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="push-status-row">
                <div className="bg-[color:var(--bg-elevated)] rounded-lg px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-semibold">Behörighet</p>
                  <p className={`text-xs font-medium ${
                    pushStatus.permission === "granted" ? "text-[color:var(--moss)]" :
                    pushStatus.permission === "denied" ? "text-[#DC2626]" : "text-[color:var(--text-secondary)]"
                  }`}>
                    {pushStatus.permission === "granted" ? "Tillåten" :
                     pushStatus.permission === "denied" ? "Nekad" :
                     pushStatus.permission === "default" ? "Inte frågad" : "—"}
                  </p>
                </div>
                <div className="bg-[color:var(--bg-elevated)] rounded-lg px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-semibold">Webbläsare</p>
                  <p className={`text-xs font-medium ${pushStatus.supported ? "text-[color:var(--moss)]" : "text-[#DC2626]"}`}>
                    {pushStatus.supported ? "Stöds" : "Stöds ej"}
                  </p>
                </div>
                <div className="bg-[color:var(--bg-elevated)] rounded-lg px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-semibold">Token</p>
                  <p className={`text-xs font-medium ${localStorage.getItem("grindstugatan_push_token") ? "text-[color:var(--moss)]" : "text-[color:var(--text-secondary)]"}`}>
                    {localStorage.getItem("grindstugatan_push_token") ? "Registrerad" : "Saknas"}
                  </p>
                </div>
              </div>

              {/* Enhets-ID — för verifiering mot DB */}
              {(() => {
                const tok = localStorage.getItem("grindstugatan_push_token");
                if (!tok) return null;
                const shortId = tok.slice(-8);
                return (
                  <div className="mt-2 flex items-center gap-2 bg-[color:var(--bg-elevated)] rounded-lg px-3 py-2" data-testid="push-device-id-row">
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-semibold">Enhets-ID</p>
                      <p className="text-sm font-mono font-medium tracking-wide" data-testid="push-device-id-short">
                        …{shortId}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(tok);
                          toast.success("Kopierat — klistra in i admin/DB för att matcha enheten");
                        } catch (_) {
                          toast.error("Kunde inte kopiera");
                        }
                      }}
                      className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-white border border-[color:var(--border)] hover:bg-[color:var(--bg-elevated)] flex-shrink-0"
                      data-testid="push-device-id-copy"
                      title="Kopiera hela push-token till urklipp"
                    >
                      📋 Kopiera helt ID
                    </button>
                  </div>
                );
              })()}

              {/* Knapprad — alltid synliga */}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={runTestPush}
                  disabled={pushBusy || !pushEnabled}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-[color:var(--moss)] text-white hover:bg-[#1f421f] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="push-test-button"
                  title={!pushEnabled ? "Slå på push först" : "Skicka en testnotis till denna enhet"}
                >
                  📨 Skicka testnotis
                </button>
                <button
                  onClick={reRegisterPush}
                  disabled={pushBusy || !pushStatus?.supported}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-[color:var(--border)] hover:bg-[color:var(--bg-elevated)] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="push-reregister-button"
                  title="Avregistrera och registrera om enheten — fixar de flesta problem"
                >
                  {pushBusy ? "Registrerar…" : "🔄 Registrera om enhet"}
                </button>
                <button
                  onClick={runPushDiagnostics}
                  disabled={diagBusy}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-[color:var(--border)] hover:bg-[color:var(--bg-elevated)] disabled:opacity-40"
                  data-testid="push-diagnostics-button"
                >
                  {diagBusy ? "Diagnostiserar…" : "🩺 Diagnostisera"}
                </button>
              </div>
              {diagSteps && (
                <div className="mt-3 bg-[color:var(--bg-elevated)] rounded-xl p-3 border border-[color:var(--border)] space-y-2" data-testid="push-diag-results">
                  <p className="text-xs font-semibold text-[color:var(--text-secondary)]">Diagnostik-resultat</p>
                  {diagSteps.map((s, i) => (
                    <div key={`diag-${i}-${s.name || ""}`} className="text-xs flex items-start gap-2" data-testid={`push-diag-step-${i}`}>
                      <span className={`mt-0.5 flex-shrink-0 ${
                        s.status === "ok" ? "text-[color:var(--moss)]" :
                        s.status === "fail" ? "text-[#D44A3A]" :
                        s.status === "warn" ? "text-[#E07A5F]" :
                        "text-[color:var(--text-tertiary)]"
                      }`}>
                        {s.status === "ok" ? "✓" : s.status === "fail" ? "✗" : s.status === "warn" ? "!" : "○"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{s.name}</p>
                        {s.detail && <p className="text-[color:var(--text-tertiary)] break-words">{s.detail}</p>}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => setDiagSteps(null)}
                    className="text-[10px] text-[color:var(--text-tertiary)] hover:underline"
                  >
                    Stäng
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Quiet hours — bara admin */}
      {currentUser?.role === "admin" && (
        <div className="mb-6" data-testid="quiet-hours-section">
          <h3 className="font-display text-base font-medium mb-2">Tysta timmar</h3>
          <div className="card-soft p-4">
            <QuietHoursPanel />
          </div>
        </div>
      )}

      {/* Mina enheter */}
      <div className="mb-6" data-testid="devices-section">
        <h3 className="font-display text-base font-medium mb-2">Mina enheter</h3>
        <div className="card-soft p-4">
          <p className="text-xs text-[color:var(--text-secondary)] mb-3 leading-relaxed">
            Varje enhet du aktiverar push på registreras här. Du kan döpa dem, skicka riktade testnotiser eller koppla bort gamla enheter.
          </p>
          <DevicesPanel />
        </div>
      </div>

      {/* Alla familjens enheter (admin) */}
      {currentUser?.role === "admin" && (
        <div className="mb-6" data-testid="all-devices-section">
          <h3 className="font-display text-base font-medium mb-2">Alla familjens enheter</h3>
          <div className="card-soft p-4">
            <p className="text-xs text-[color:var(--text-secondary)] mb-3 leading-relaxed">
              Som admin ser du varje familjemedlems enheter. Pausa push för en specifik enhet, eller ta bort den helt — t.ex. för att stoppa notiser på barnens skärmar inför läggdags eller koppla bort en gammal telefon.
            </p>
            <AllDevicesPanel />
          </div>
        </div>
      )}
    </>
  );
}
